import { memo } from "react";
import { CreditCard, Building, Banknote, CheckCircle2, Shield, Zap } from "lucide-react";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { getBookingTheme, BookingTheme } from "@/config/bookingThemes";

interface PaymentMethodSelectorProps {
  value: "stripe" | "paypal" | "in_person";
  onChange: (value: "stripe" | "paypal" | "in_person") => void;
  amount: number;
  serviceName: string;
  showPayPal?: boolean;
  theme?: BookingTheme;
  className?: string;
}

interface PaymentMethodCardProps {
  value: string;
  icon: React.ReactNode;
  title: string;
  description: string;
  isSelected: boolean;
  badges?: string[];
  iconBgClass?: string;
}

const PaymentMethodCard = memo(({ 
  value, 
  icon, 
  title, 
  description,
  isSelected,
  badges,
  iconBgClass,
}: PaymentMethodCardProps) => (
  <div 
    className={cn(
      "relative overflow-hidden rounded-xl border-2 p-4 sm:p-5 transition-all duration-300 cursor-pointer touch-manipulation",
      "hover:shadow-lg hover:border-primary/50",
      isSelected && "border-primary bg-primary/5 ring-2 ring-primary/20"
    )}
  >
    {/* Top gradient line */}
    <div className={cn(
      "absolute top-0 left-0 w-full h-1 transition-opacity duration-300",
      "bg-gradient-to-r from-primary to-accent",
      isSelected ? "opacity-100" : "opacity-0"
    )} />
    
    <div className="flex items-center gap-4">
      <RadioGroupItem value={value} id={value} className="flex-shrink-0" />
      
      {/* Icon container with themed background */}
      <div className={cn(
        "w-12 h-12 sm:w-14 sm:h-14 rounded-xl flex items-center justify-center flex-shrink-0",
        "transition-transform duration-300",
        isSelected && "scale-105",
        iconBgClass
      )}>
        {icon}
      </div>
      
      <Label htmlFor={value} className="flex-1 cursor-pointer">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-semibold text-base">{title}</span>
          {badges && badges.map((badge) => (
            <Badge key={badge} variant="secondary" className="text-xs rounded-full">
              {badge}
            </Badge>
          ))}
        </div>
        <p className="text-sm text-muted-foreground mt-1">{description}</p>
      </Label>
      
      {isSelected && (
        <CheckCircle2 className="h-6 w-6 text-primary flex-shrink-0" />
      )}
    </div>
  </div>
));
PaymentMethodCard.displayName = "PaymentMethodCard";

export const PaymentMethodSelector = memo(function PaymentMethodSelector({
  value,
  onChange,
  amount,
  serviceName,
  showPayPal = false,
  theme,
  className,
}: PaymentMethodSelectorProps) {
  const activeTheme = theme || getBookingTheme("trainer");
  
  return (
    <div className={cn("space-y-4", className)}>
      <Label className="text-base font-semibold flex items-center gap-2">
        <div className={cn(
          "w-8 h-8 rounded-lg flex items-center justify-center",
          `bg-gradient-to-br ${activeTheme.gradient}`
        )}>
          <CreditCard className="h-4 w-4 text-white" />
        </div>
        Método de pago
      </Label>
      
      <RadioGroup
        value={value}
        onValueChange={(v) => onChange(v as "stripe" | "paypal" | "in_person")}
        className="space-y-3"
      >
        <PaymentMethodCard
          value="stripe"
          icon={<CreditCard className="h-6 w-6 sm:h-7 sm:w-7 text-[#635BFF]" />}
          title="Tarjeta de crédito/débito"
          description="Pago seguro con Stripe"
          isSelected={value === "stripe"}
          badges={["Seguro", "Rápido"]}
          iconBgClass="bg-gradient-to-br from-[#635BFF]/10 to-[#635BFF]/5"
        />

        {showPayPal && (
          <PaymentMethodCard
            value="paypal"
            icon={
              <svg className="h-6 w-6 sm:h-7 sm:w-7" viewBox="0 0 24 24" fill="currentColor">
                <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944 3.72a.765.765 0 0 1 .757-.651h6.77c2.898 0 4.9 1.928 4.563 4.398-.49 3.564-3.02 5.566-6.355 5.566H8.59l-.988 7.527a.504.504 0 0 1-.526.777zM18.11 8.008c-.312 2.264-1.898 4.018-4.403 4.018h-2.29l-.824 5.27h2.127c.24 0 .446-.168.483-.406l.02-.12.383-2.459.025-.13a.495.495 0 0 1 .489-.408h.307c1.994 0 3.554-.82 4.012-3.19.192-.99.092-1.816-.328-2.395-.127-.176-.284-.325-.476-.45z" fill="#003087"/>
              </svg>
            }
            title="PayPal"
            description="Paga con tu cuenta PayPal"
            isSelected={value === "paypal"}
            iconBgClass="bg-gradient-to-br from-[#003087]/10 to-[#009CDE]/5"
          />
        )}

        <PaymentMethodCard
          value="in_person"
          icon={<Banknote className="h-6 w-6 sm:h-7 sm:w-7 text-primary" />}
          title="Pago presencial"
          description="Paga directamente en el centro"
          isSelected={value === "in_person"}
          badges={["Efectivo", "Tarjeta"]}
          iconBgClass={`bg-gradient-to-br ${activeTheme.gradientLight}`}
        />
      </RadioGroup>

      {/* Premium Price Summary Card */}
      <Card className="overflow-hidden border-2 border-primary/20">
        <div className={cn(
          "h-1.5 w-full",
          `bg-gradient-to-r ${activeTheme.gradient}`
        )} />
        <CardContent className="p-4 sm:p-5 space-y-3">
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">{serviceName}</span>
            <span className="font-medium">{amount.toFixed(2)}€</span>
          </div>
          <Separator />
          <div className="flex justify-between items-center">
            <span className="font-semibold">Total a pagar</span>
            <span className={cn(
              "text-2xl sm:text-3xl font-bold",
              `bg-gradient-to-r ${activeTheme.gradient} bg-clip-text text-transparent`
            )}>
              {amount.toFixed(2)}€
            </span>
          </div>
          
          {/* Trust indicators */}
          <div className="flex items-center justify-center gap-4 pt-2 text-xs text-muted-foreground">
            <span className="flex items-center gap-1">
              <Shield className="h-3.5 w-3.5 text-primary" />
              Pago seguro
            </span>
            <span className="flex items-center gap-1">
              <Zap className="h-3.5 w-3.5 text-secondary" />
              Confirmación inmediata
            </span>
          </div>
        </CardContent>
      </Card>
    </div>
  );
});
